// Global Instructions Rule Applied!

import {
  FieldTypeEnum,
  FieldConfig,
  CityFieldConfig,
  CountryFieldConfig,
  CompanyFieldConfig
} from '../../../core/components/InputManager/types.ts'
import { ContactRecordFields } from '../../../state_manager/data_models/contact_record.ts'
import {
  transformCompanyTeamEntryToCompanySelect,
  transformCompanyTeamEntriesToCompanySelects
} from './contactFormTransformers.ts'
import { processContactCompanyRelationships, transformFounderOfToAPIFormat } from '../services/contactsService.ts'
import { RELATIONSHIP_TYPE_FOUNDER } from '../../../state_manager/globals.ts'

/**
 * Field configuration utilities for Contact Drawer InputManager integration
 * Generates InputManager field configurations for different sections of the drawer
 */

/**
 * Generates field configurations for Contact Information section
 */
export function getContactInfoFields(
  formData: any,
  helpers: any,
  onFounderOfChange?: (field: string, value: any) => Promise<void>
): FieldConfig[] {
  const fields: FieldConfig[] = [
    // First name
    {
      key: ContactRecordFields.contacts__first_name,
      label: 'First name',
      type: FieldTypeEnum.TEXT,
      value: formData[ContactRecordFields.contacts__first_name] || '',
      editable: true,
      autoFocus: true
    },
    // Middle name
    {
      key: ContactRecordFields.contacts__middle_name,
      label: 'Middle name',
      type: FieldTypeEnum.TEXT,
      value: formData[ContactRecordFields.contacts__middle_name] || '',
      editable: true
    },
    // Last name (required)
    {
      key: ContactRecordFields.contacts__last_name,
      label: 'Last name',
      type: FieldTypeEnum.TEXT,
      value: formData[ContactRecordFields.contacts__last_name] || '',
      required: true,
      editable: true
    },
    // Preferred name (defaults to first name)
    {
      key: ContactRecordFields.contacts__preferred_name,
      label: 'Preferred name',
      type: FieldTypeEnum.TEXT,
      value: formData[ContactRecordFields.contacts__preferred_name] || '',
      editable: true
    },
    // Email
    {
      key: ContactRecordFields.contacts__email,
      label: 'Email',
      type: FieldTypeEnum.EMAIL,
      value: formData[ContactRecordFields.contacts__email] || '',
      editable: true
    },
    // Email (other)
    {
      key: ContactRecordFields.contacts__email_other,
      label: 'Email (other)',
      type: FieldTypeEnum.EMAIL,
      value: formData[ContactRecordFields.contacts__email_other] || '',
      editable: true
    },
    // Email (private)
    {
      key: ContactRecordFields.contacts__email_private,
      label: 'Email (private)',
      type: FieldTypeEnum.EMAIL,
      value: formData[ContactRecordFields.contacts__email_private] || '',
      editable: true
    },
    // Business phone
    {
      key: ContactRecordFields.contacts__business_phone,
      label: 'Business phone',
      type: FieldTypeEnum.PHONE,
      value: formData[ContactRecordFields.contacts__business_phone] || '',
      editable: true
    },
    // Mobile phone
    {
      key: ContactRecordFields.contacts__mobile_phone,
      label: 'Mobile phone',
      type: FieldTypeEnum.PHONE,
      value: formData[ContactRecordFields.contacts__mobile_phone] || '',
      editable: true
    },
    // LinkedIn
    {
      key: ContactRecordFields.contacts__linked_in_url,
      label: 'LinkedIn',
      type: FieldTypeEnum.URL,
      value: formData[ContactRecordFields.contacts__linked_in_url] || '',
      editable: true
    },
    // Founder of
    {
      key: 'prog__founder_of',
      label: 'Founder of',
      type: FieldTypeEnum.COMPANY,
      mode: 'multiple',
      value: formData.prog__founder_of
        ? Array.isArray(formData.prog__founder_of)
          ? transformCompanyTeamEntriesToCompanySelects(formData.prog__founder_of)
          : []
        : [],
      editable: true,
      getCompanyDetailsById: helpers?.getCompanyDetailsById,
      hyperlinked: true,
      hyperlinkModule: 'companies',
      getRecordId: (value: any) => value?.value || value?.id || null,
      // Disable auto-patch for this field by setting patchEndpoint to empty string
      // This prevents InputManager from trying to PATCH /prog endpoint
      patchEndpoint: ''
    } as CompanyFieldConfig,
    // Notes
    {
      key: ContactRecordFields.contacts__notes,
      label: 'Notes',
      type: FieldTypeEnum.TEXTAREA,
      value: formData[ContactRecordFields.contacts__notes] || '',
      editable: true,
      rows: 15,
      autoSize: { minRows: 15, maxRows: 25 }
    }
  ]

  return fields
}

/**
 * Generates field configurations for Address section
 */
export function getAddressFields(formData: any, helpers: any, isReadOnly = false): FieldConfig[] {
  return [
    // Street 1
    {
      key: ContactRecordFields.contacts__address_1,
      label: 'Street 1',
      type: FieldTypeEnum.TEXT,
      value: formData[ContactRecordFields.contacts__address_1] || '',
      editable: !isReadOnly
    },
    // Street 2
    {
      key: ContactRecordFields.contacts__address_2,
      label: 'Street 2',
      type: FieldTypeEnum.TEXT,
      value: formData[ContactRecordFields.contacts__address_2] || '',
      editable: !isReadOnly
    },
    // Street 3
    {
      key: ContactRecordFields.contacts__address_3,
      label: 'Street 3',
      type: FieldTypeEnum.TEXT,
      value: formData[ContactRecordFields.contacts__address_3] || '',
      editable: !isReadOnly
    },
    // City
    {
      key: ContactRecordFields.contacts__address_city_label,
      label: 'City',
      type: FieldTypeEnum.CITY,
      value: formData[ContactRecordFields.contacts__address_city_label] || '',
      editable: !isReadOnly,
      country: formData[ContactRecordFields.contacts__address_country_code] || '',
      patchFields: [
        { sourceKey: 'cityCode', targetField: 'address_city_code' },
        { sourceKey: 'cityName', targetField: 'address_city_label' },
        { sourceKey: 'countryCode', targetField: 'address_country_code' },
        { sourceKey: 'state', targetField: 'address_stateprovince' }
      ],
      patchEndpoint: 'contacts',
      valueTransformer: (cityObject: any, currentRecord?: any) => {
        const isClearing = !cityObject || cityObject === '' || (typeof cityObject === 'object' && !cityObject.value)

        const cityCode = isClearing ? null : (cityObject?.value ?? null)
        const cityLabel = isClearing ? null : cityObject?.name || cityObject?.label || null
        const countryCode = isClearing ? undefined : (cityObject?.data?.countryCode ?? undefined)
        const state = isClearing ? null : (cityObject?.data?.state ?? null) || null

        const result: Record<string, any> = {
          cityCode,
          cityName: cityLabel,
          state,
          [ContactRecordFields.contacts__address_city_label]: cityLabel,
          [ContactRecordFields.contacts__address_stateprovince]: state
        }

        if (!isClearing && countryCode !== undefined) {
          result.countryCode = countryCode
          result[ContactRecordFields.contacts__address_country_code] = countryCode
        }

        return result
      }
    } as CityFieldConfig,
    // State / Province
    {
      key: ContactRecordFields.contacts__address_stateprovince,
      label: 'State / Province',
      type: FieldTypeEnum.TEXT,
      value: formData[ContactRecordFields.contacts__address_stateprovince] || '',
      editable: !isReadOnly
    },
    // ZIP Code
    {
      key: ContactRecordFields.contacts__address_postal_code,
      label: 'ZIP Code',
      type: FieldTypeEnum.TEXT,
      value: formData[ContactRecordFields.contacts__address_postal_code] || '',
      editable: !isReadOnly
    },
    // Country
    {
      key: ContactRecordFields.contacts__address_country_label,
      label: 'Country',
      type: FieldTypeEnum.COUNTRY,
      value: formData[ContactRecordFields.contacts__address_country_label] || '',
      editable: !isReadOnly,
      patchFields: [
        { sourceKey: 'countryCode', targetField: 'address_country_code' },
        { sourceKey: 'cityCode', targetField: 'address_city_code' },
        { sourceKey: 'state', targetField: 'address_stateprovince' }
      ],
      patchEndpoint: 'contacts',
      valueTransformer: (countryObject: any) => {
        const isClearing =
          !countryObject || countryObject === '' || (typeof countryObject === 'object' && !countryObject.value)
        const countryCode = isClearing ? null : (countryObject?.value ?? null)
        const countryLabel = isClearing ? null : (countryObject?.label ?? null)

        return {
          countryCode,
          cityCode: null,
          state: null,
          [ContactRecordFields.contacts__address_country_code]: countryCode,
          [ContactRecordFields.contacts__address_country_label]: countryLabel,
          [ContactRecordFields.contacts__address_city_code]: null,
          [ContactRecordFields.contacts__address_city_label]: null,
          [ContactRecordFields.contacts__address_stateprovince]: null
        }
      }
    } as CountryFieldConfig
  ]
}
